import React, { MouseEventHandler } from "react";
import Link from "next/link";

import { useEventTracker } from "@/hooks/useEventTracker";
import { useDispatch, useSelector } from "@/redux/hooks";
import { getFormattedLocation } from "@/redux/selectors/location";
import { requestLocation } from "@/redux/slices/location";

type Props = {
  title: string;
  subtitle?: string;
  link?: string;
  linkText?: string;
  onClickEvents?: MouseEventHandler<HTMLAnchorElement>;
  changeLocation?: boolean;
  className?: string;
  children: React.ReactNode;
};

const SectionWrapper = ({
  title,
  subtitle,
  link,
  linkText,
  onClickEvents,
  changeLocation = true,
  className,
  children,
}: Props) => {
  const formattedLocation = useSelector(getFormattedLocation);
  const dispatch = useDispatch();
  const { publishEvent } = useEventTracker();

  const onClickHandler = () => {
    dispatch(requestLocation());
    publishEvent({
      action: "Click",
      category: "Change location button",
      label: title,
    });
  };

  return (
    <div className={className} data-testid="section-wrapper">
      <div className="flex justify-between items-end">
        <div>
          {title && title.trim().length > 0 && (
            <h2 data-testid="section-wrapper-title" className={"heading--m"}>
              {title}
            </h2>
          )}
          {subtitle && subtitle.trim().length > 0 ? (
            <p className="mb-none" data-testid="section-wrapper-subtitle">
              {subtitle}
              {changeLocation ? (
                <>
                  {formattedLocation ? ` ${formattedLocation}` : " your city"} (
                  <button
                    onClick={onClickHandler}
                    className="font-bold pointer text-green"
                    data-testid="section-wrapper-location-change"
                  >
                    change
                  </button>
                  )
                </>
              ) : null}
            </p>
          ) : null}
        </div>
        {link && (
          <Link
            className="font-bold hidden md:block text-green"
            href={link}
            onClick={onClickEvents}
            data-testid="section-wrapper-link"
          >
            {linkText}
          </Link>
        )}
      </div>
      {children}
      {link && (
        <Link
          className="mt-sm font-bold block md:hidden text-green"
          href={link}
          onClick={onClickEvents}
        >
          {linkText}
        </Link>
      )}
    </div>
  );
};

export default SectionWrapper;
